import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShoppingBasket, Crown, Trash2, X, Check, Loader2, Lock, Printer, Share2 } from 'lucide-react';
import { API, useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

function PremiumGate({ authenticated }) {
  const navigate = useNavigate();
  const { openAuth } = useAuth();
  return (
    <div className="max-w-lg mx-auto px-5 py-20 text-center min-h-[70vh] flex flex-col items-center justify-center" data-testid="shopping-premium-gate">
      <span className="w-16 h-16 rounded-2xl bg-botanica-greenLight flex items-center justify-center mb-5">
        <Lock className="w-8 h-8 text-botanica-green" />
      </span>
      <h1 className="font-serif text-3xl text-botanica-ink mb-2">Shopping List is a Premium feature</h1>
      <p className="text-sm text-botanica-muted mb-7 max-w-sm">
        Upgrade to Premium to send recipe ingredients straight to a smart shopping list that syncs across your devices.
      </p>
      {authenticated ? (
        <button
          onClick={() => navigate('/membership')}
          className="inline-flex items-center gap-2 bg-botanica-green text-white px-7 py-3 rounded-full text-sm font-medium hover:bg-botanica-greenDark transition-colors"
          data-testid="shopping-upgrade-btn"
        >
          <Crown className="w-4 h-4" /> Upgrade to Premium
        </button>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <button
            onClick={() => navigate('/membership')}
            className="inline-flex items-center gap-2 bg-botanica-green text-white px-7 py-3 rounded-full text-sm font-medium hover:bg-botanica-greenDark transition-colors"
            data-testid="shopping-upgrade-btn"
          >
            <Crown className="w-4 h-4" /> See Premium plans
          </button>
          <button onClick={() => openAuth('login')} className="text-sm text-botanica-muted hover:text-botanica-ink transition-colors">
            Already Premium? Sign in
          </button>
        </div>
      )}
    </div>
  );
}

export default function ShoppingList() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | gated
  const [gateAuth, setGateAuth] = useState(false);

  const load = useCallback(async () => {
    if (!user) {
      setGateAuth(false);
      setState('gated');
      return;
    }
    try {
      const res = await axios.get(`${API}/shopping-list`);
      setItems(res.data.items || []);
      setState('ready');
    } catch (e) {
      if (e?.response?.status === 403) {
        setGateAuth(true);
        setState('gated');
      } else if (e?.response?.status === 401) {
        setGateAuth(false);
        setState('gated');
      } else {
        setState('ready');
      }
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) load();
  }, [authLoading, load]);

  const toggleItem = async (id) => {
    setItems((prev) => prev.map((i) => (i.item_id === id ? { ...i, checked: !i.checked } : i)));
    try {
      const res = await axios.patch(`${API}/shopping-list/item/${id}`);
      setItems(res.data.items || []);
    } catch (e) { load(); }
  };

  const removeItem = async (id) => {
    setItems((prev) => prev.filter((i) => i.item_id !== id));
    try {
      await axios.delete(`${API}/shopping-list/item/${id}`);
    } catch (e) { load(); }
  };

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/shopping-list`);
      setItems([]);
      toast.message('Shopping list cleared.');
    } catch (e) { load(); }
  };

  const buildGroups = () =>
    items.reduce((acc, it) => {
      const key = it.recipe_name || 'Other';
      (acc[key] = acc[key] || []).push(it);
      return acc;
    }, {});

  const buildText = () => {
    const g = buildGroups();
    const lines = ['Shopping List — Your Herbal Companion', ''];
    Object.entries(g).forEach(([recipe, list]) => {
      lines.push(recipe.toUpperCase());
      list.forEach((it) => lines.push(`${it.checked ? '[x]' : '[ ]'} ${it.text}`));
      lines.push('');
    });
    return lines.join('\n').trim();
  };

  const handlePrint = () => {
    const g = buildGroups();
    const win = window.open('', '_blank', 'width=600,height=800');
    if (!win) { toast.error('Please allow pop-ups to print your list.'); return; }
    const sections = Object.entries(g).map(([recipe, list]) => `
      <h2>${recipe}</h2>
      <ul>${list.map((it) => `<li class="${it.checked ? 'done' : ''}"><span class="box">${it.checked ? '&#10003;' : ''}</span>${it.text}</li>`).join('')}</ul>
    `).join('');
    win.document.write(`
      <html><head><title>Shopping List</title>
      <style>
        body{font-family:Georgia,serif;color:#2f3b30;padding:32px;max-width:520px;margin:auto;}
        h1{font-size:26px;margin:0 0 4px;}
        .sub{color:#7a8a76;font-size:13px;margin-bottom:24px;}
        h2{font-size:12px;letter-spacing:.12em;text-transform:uppercase;color:#4a7a52;margin:22px 0 8px;}
        ul{list-style:none;padding:0;margin:0;}
        li{display:flex;align-items:center;gap:10px;padding:7px 0;border-bottom:1px solid #eee;font-size:15px;}
        .box{display:inline-block;width:16px;height:16px;border:1.5px solid #9aa896;border-radius:4px;text-align:center;line-height:16px;font-size:12px;color:#4a7a52;}
        li.done{color:#9aa896;text-decoration:line-through;}
      </style></head>
      <body onload="window.print()">
        <h1>Shopping List</h1>
        <div class="sub">Your Herbal Companion · ${items.length} items</div>
        ${sections}
      </body></html>`);
    win.document.close();
  };

  const handleShare = async () => {
    const text = buildText();
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Shopping List', text });
        return;
      } catch (e) {
        if (e?.name === 'AbortError') return; // user cancelled
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Shopping list copied to clipboard.');
    } catch (e) {
      toast.error('Could not share the list on this device.');
    }
  };

  if (authLoading || state === 'loading') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-botanica-green animate-spin" />
      </div>
    );
  }

  if (state === 'gated') return <PremiumGate authenticated={gateAuth} />;

  // group by recipe
  const groups = items.reduce((acc, it) => {
    const key = it.recipe_name || 'Other';
    (acc[key] = acc[key] || []).push(it);
    return acc;
  }, {});
  const checkedCount = items.filter((i) => i.checked).length;

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 min-h-[70vh]" data-testid="shopping-list-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl text-botanica-ink mb-1 flex items-center gap-2">
            <ShoppingBasket className="w-7 h-7 text-botanica-green" /> Shopping List
          </h1>
          <p className="text-sm text-botanica-muted">
            {items.length === 0 ? 'Nothing here yet.' : `${checkedCount} of ${items.length} items checked`}
          </p>
        </div>
        {items.length > 0 && (
          <div className="flex items-center gap-2 shrink-0 mt-2">
            <button
              onClick={handleShare}
              className="flex items-center gap-1.5 text-sm text-botanica-muted hover:text-botanica-green transition-colors px-2 py-1 rounded-full hover:bg-botanica-card/60"
              data-testid="shopping-share-btn"
              title="Share your list"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 text-sm text-botanica-muted hover:text-botanica-green transition-colors px-2 py-1 rounded-full hover:bg-botanica-card/60"
              data-testid="shopping-print-btn"
              title="Print your list"
            >
              <Printer className="w-4 h-4" /> Print
            </button>
            <button
              onClick={clearAll}
              className="flex items-center gap-1.5 text-sm text-botanica-muted hover:text-botanica-warn transition-colors px-2 py-1 rounded-full hover:bg-botanica-card/60"
              data-testid="shopping-clear-btn"
            >
              <Trash2 className="w-4 h-4" /> Clear all
            </button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20">
          <span className="w-16 h-16 rounded-2xl bg-botanica-card/70 flex items-center justify-center mx-auto mb-4">
            <ShoppingBasket className="w-8 h-8 text-botanica-muted" />
          </span>
          <p className="text-sm text-botanica-muted mb-4">Open a recipe and tap “Add ingredients to shopping list” to start.</p>
          <button
            onClick={() => (window.location.href = '/recipes')}
            className="inline-flex items-center gap-2 bg-botanica-green text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-botanica-greenDark transition-colors"
            data-testid="shopping-browse-recipes-btn"
          >
            Browse recipes
          </button>
        </div>
      ) : (
        <div className="space-y-7">
          {Object.entries(groups).map(([recipeName, list]) => (
            <div key={recipeName}>
              <p className="text-[11px] uppercase tracking-widest text-botanica-green font-semibold mb-3">{recipeName}</p>
              <ul className="space-y-2">
                {list.map((it) => (
                  <li
                    key={it.item_id}
                    className="flex items-center gap-3 bg-botanica-card/60 hover:bg-botanica-card rounded-xl px-4 py-3 transition-colors group"
                    data-testid={`shopping-item-${it.item_id}`}
                  >
                    <button
                      onClick={() => toggleItem(it.item_id)}
                      className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                        it.checked ? 'bg-botanica-green border-botanica-green text-white' : 'border-botanica-muted/50 hover:border-botanica-green'
                      }`}
                      data-testid={`shopping-check-${it.item_id}`}
                    >
                      {it.checked && <Check className="w-3.5 h-3.5" />}
                    </button>
                    <span className={`flex-1 text-sm ${it.checked ? 'line-through text-botanica-muted' : 'text-botanica-ink'}`}>
                      {it.text}
                    </span>
                    <button
                      onClick={() => removeItem(it.item_id)}
                      className="p-1 rounded-full text-botanica-muted hover:text-botanica-warn hover:bg-botanica-panel transition-colors opacity-0 group-hover:opacity-100"
                      data-testid={`shopping-remove-${it.item_id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
